import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Container, Paper, Typography, Box, Button, 
  Divider, Chip, Grid, Card, CardContent,
  List, ListItem, ListItemText, ListItemIcon,
  useMediaQuery, useTheme, Zoom, Fade, Fab,Badge
} from '@mui/material';
import { 
  CheckCircle, LocalShipping, Payment, 
  Store, Print, ShoppingBag, ArrowBack,
  Discount, Savings, Games, ShoppingCart,
  RestaurantMenu, AccessTime, LocationOn,
  CreditCard, Receipt, EmojiEvents
} from '@mui/icons-material';
import { motion } from 'framer-motion';

import api from '../services/api';
import noPhoto from "../assets/no_photo.webp";
import { useAuth } from '../context/AuthContext';
import Lottie from 'lottie-react';
// Import your Lottie JSON file (download from lottiefiles.com)
import successAnimation from '../assets/success-checkmark.json';
import { formatPrice } from '../components/CarrancyTrans';

const OrderConfirmation = ({keywordsData, language}) => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const saveOrderToLocalStorage = (orderData) => {
  // Get existing orders
  const existingOrders = JSON.parse(localStorage.getItem('user_orders') || '[]');
  
  // Prepare order data
  const newOrder = {
    id: orderData.id || Date.now(),
    order_number: orderData.order_number || `ORD-${Date.now()}`,
    restaurant_id: orderData.restaurant_id,
    restaurant_name: orderData.restaurant_name,
    restaurant_name_en: orderData.restaurant_name_en,
    total_amount: orderData.total_amount,
    currency: orderData.currency || 'MYR',
    status: orderData.status || 'processing',
    order_date: orderData.order_date || new Date().toISOString(),
    items: orderData.items || [],
    items_count: orderData.items?.length || 0,
    payment_method: orderData.payment_method,
    delivery_address: orderData.delivery_address
  };
  
  // Add to existing orders (prevent duplicates)
  const updatedOrders = [newOrder, ...existingOrders.filter(o => o.id !== newOrder.id)];
  
  // Save back to localStorage
  localStorage.setItem('user_orders', JSON.stringify(updatedOrders));
  
  // Also save individual order info (for compatibility with your existing code)
  localStorage.setItem('order_info', JSON.stringify(newOrder));
};

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const response = await api.get(`/orders/${orderId}`);
        setOrder(response.data);
        saveOrderToLocalStorage(response.data);
      } catch (err) {
        console.error('Error fetching order:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderId]);

  // Calculate total savings from order items
  const totalSavings = order?.items?.reduce((total, item) => {
    return total + ((item.original_price || item.price) - item.price) * item.quantity;
  }, 0) || 0;

  // Calculate original total before discounts
  const originalTotal = order?.items?.reduce((total, item) => {
    return total + (item.original_price || item.price) * item.quantity;
  }, 0) || 0;

  if (loading) return (
    <Container maxWidth="md" sx={{ 
      py: 4, 
      textAlign: 'center',
      backgroundColor: '#000000',
      minHeight: '100vh',
      color: '#FFFFFF'
    }}>
      <Typography variant="h6" sx={{ color: '#B3B3B3' }}>Loading order details...</Typography>
    </Container>
  );

  const statusTxt = {
    canceled: language === 'ar' ? 'ملغي' : 'Cancelled',
    pending: language === 'ar' ? 'معلق' : 'Pending',
    processing: language === 'ar' ? 'قيد التحضير' : 'Preparation',
    shipped: language === 'ar' ? 'قيد التوصيل' : 'On the way',
    delivered: language === 'ar' ? 'مكتمل' : 'Completed'
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'delivered':
        return { bg: '#00C853', text: '#000000' };
      case 'canceled':
        return { bg: '#FF3D00', text: '#FFFFFF' };
      case 'processing':
        return { bg: '#FFD700', text: '#000000' };
      case 'shipped':
        return { bg: '#2196F3', text: '#FFFFFF' };
      default:
        return { bg: '#2D2D2D', text: '#FFFFFF' };
    }
  };
  
  if (!order) return (
    <Container maxWidth="md" sx={{ 
      py: 4, 
      textAlign: 'center',
      backgroundColor: '#000000',
      minHeight: '100vh'
    }}>
      <Typography variant="h6" sx={{ color: '#FF3D00' }}>{language === 'ar' ? 'لم يتم العثور على الطلب' : 'Order not found'}</Typography>
      <Button 
        variant="contained" 
        sx={{ 
          mt: 2,
          backgroundColor: '#FFD700',
          color: '#000000',
          fontWeight: 700,
          '&:hover': {
            backgroundColor: '#FFE55C',
            boxShadow: '0 4px 12px rgba(255, 215, 0, 0.3)',
          }
        }} 
        onClick={() => navigate('/')}
      >
        {language === 'ar' ? 'إستمرار التسوق' : 'Continue Shopping'}
      </Button>
    </Container>
  );

  return (
     <Container maxWidth="lg" sx={{ 
      py: { xs: 2, sm: 3, md: 4 }, 
      px: { xs: 1.5, sm: 2, md: 3 },
      mb: { xs: 10, sm: 12 },
      backgroundColor: '#000000',
      minHeight: '100vh',
      color: '#FFFFFF'
    }}>
 {/* Header Section with Lottie Animation and Background */}
<Fade in timeout={500}>
  <Box sx={{ textAlign: 'center', mb: { xs: 3, sm: 4 } }}>
    {/* Animated Background Circle */}
    <Box sx={{ 
      position: 'relative',
      display: 'inline-block',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: { xs: 160, sm: 220, md: 270, lg: 320 },
        height: { xs: 160, sm: 220, md: 270, lg: 320 },
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255,215,0,0.1) 0%, rgba(255,215,0,0) 70%)',
        animation: 'pulse 2s ease-in-out infinite',
      }
    }}>
      <Box sx={{ 
        width: { xs: 140, sm: 200, md: 250, lg: 300 },
        height: { xs: 140, sm: 200, md: 250, lg: 300 },
        margin: '0 auto',
        mb: 3,
        cursor: 'pointer',
        position: 'relative',
        zIndex: 1
      }}>
        <Lottie 
          animationData={successAnimation}
          loop={false}
          autoplay={true}
          style={{ width: '100%', height: '100%' }}
          onComplete={() => console.log('Animation completed')}
        />
      </Box>
    </Box>
    
    <Typography variant={isMobile ? "h4" : "h3"} fontWeight="bold" gutterBottom sx={{ 
      color: '#FFD700',
      fontWeight: 900,
      letterSpacing: '2px',
      fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' },
      animation: 'fadeInUp 0.6s ease-out'
    }}>
      {language === 'ar' ? 'تم تأكيد الطلب !' : 'Order confirmed!'}
    </Typography>
    
    <Typography 
      variant="h6" 
      sx={{ 
        color: '#B3B3B3', 
        fontSize: { xs: '0.9rem', sm: '1rem' },
        animation: 'fadeInUp 0.8s ease-out'
      }}
    >
      {language === 'ar' ? 'شكرا لك على الشراء' : 'Thank you for your purchase'}
    </Typography>
  </Box>
</Fade>

      <Grid container spacing={3} style={{ marginRight: -16 }}>
        {/* Order Summary Card */}
        <Grid item xs={12} md={8}>
          <Paper elevation={3} sx={{ 
            p: 3, 
            borderRadius: '16px',
            backgroundColor: '#0D0D0D',
            border: '1px solid #2D2D2D',
            '&:hover': {
              border: '1px solid #FFD700',
            }
          }}>
            {/* Order Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
              <Box>
                <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ 
                  color: '#FFD700',
                  fontWeight: 700
                }}>
                  
                  {language === 'ar' ? 'ملخص طلبك' : 'Order sammary'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                  {language === 'ar' ? 'طلب رقم #:' : '# Order ID:'} {order.id}
                </Typography>
                <Chip 
                  label={statusTxt[order.status]} 
                  sx={{ 
                    mt: 1,
                    backgroundColor: getStatusColor(order.status).bg,
                    color: getStatusColor(order.status).text,
                    fontWeight: 'bold',
                    border: 'none'
                  }}
                />
              </Box>
              <Typography variant="h5" sx={{ 
                color: '#FFD700',
                fontWeight: 800
              }}>
                {formatPrice(order.total_amount,order.currency,language)}
              </Typography>
            </Box>

            <Divider sx={{ 
              my: 2, 
              backgroundColor: '#2D2D2D',
              height: '1px'
            }} />

            {/* Savings Banner */}
            {totalSavings > 0 && (
              <Box sx={{ 
                backgroundColor: '#0D0D0D',
                border: '1px solid #FFD700',
                p: 2.5, 
                borderRadius: '12px', 
                mb: 3,
                display: 'flex',
                alignItems: 'center',
                gap: 2
              }}>
                <Savings sx={{ fontSize: 48, color: '#FFD700' }} />
                <Box>
                  <Typography variant="h6" sx={{ 
                    color: '#FFD700', 
                    fontWeight: 'bold'
                  }}>
                    {language === 'ar' ? 'لقد وفرت ' : 'You save '}{formatPrice(totalSavings.toFixed(2),order.currency,language)} !
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                    {language === 'ar' ? 'نسبة خصم ' : 'Discount value '} {((totalSavings / originalTotal) * 100).toFixed(1)}% {language === 'ar' ? 'من طلبك' : 'from your order'}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Order Items */}
            <Typography variant="h6" gutterBottom sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              color: '#FFD700',
              fontWeight: 700,
              mb: 2
            }}>
              <ShoppingBag sx={{ color: '#FFD700' }} /> {language === 'ar' ? 'منتجات الطلب' : 'Order items'}
            </Typography>
            
            <List sx={{ 
              backgroundColor: '#0D0D0D',
              borderRadius: '12px',
            }}>
              {order.items.map((item) => {
                const originalPrice = item.original_price || item.price;
                const isDiscounted = originalPrice > item.price;
                const itemSavings = (originalPrice - item.price) * item.quantity;

                return (
                  <ListItem 
                    key={item.id} 
                    sx={{ 
                      py: 2,
                      borderBottom: '1px solid #2D2D2D',
                      '&:last-child': {
                        borderBottom: 'none',
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', width: '100%', gap: 2 }}>
                      <Box sx={{ 
                        width: 70, 
                        height: 70, 
                        backgroundColor: '#1A1A1A',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <img
                          src={item.product?.imageUrl || noPhoto}
                          alt={item.product?.name}
                          style={{ 
                            width: '100%', 
                            height: '100%', 
                            objectFit: 'contain',
                            borderRadius: '8px'
                          }}
                        />
                      </Box>
                      
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle1" fontWeight="medium" sx={{ color: '#FFFFFF' }}>
                          {language === 'ar' ? item.product?.name : item.product?.name_en}
                        </Typography>
                        
                        {isDiscounted ? (
                          <Box sx={{ mt: 0.5 }}>
                            <Typography variant="body2" sx={{ 
                              color: '#FFD700', 
                              fontWeight: 'bold',
                              display: 'inline-block',
                              ml: 1
                            }}>
                              {formatPrice(item.price,order.currency,language)} × {item.quantity}
                            </Typography>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                textDecoration: 'line-through', 
                                color: '#6B6B6B',
                                display: 'block'
                              }}
                            >
                              {formatPrice(originalPrice.toFixed(2),order.currency,language)} {language === 'ar' ? ' كل' : ' each'}
                            </Typography>
                            {item.discount_percent > 0 && (
                              <Chip
                                label={language === 'ar' ? `${item.discount_percent}% خصم` : `${item.discount_percent}% off`}
                                size="small"
                                sx={{
                                  mt: 0.5,
                                  backgroundColor: '#FFD700',
                                  color: '#000000',
                                  fontWeight: 'bold',
                                  height: 22,
                                  fontSize: '0.7rem'
                                }}
                              />
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                            {formatPrice(item.price,order.currency,language)} × {item.quantity}
                          </Typography>
                        )}
                      </Box>
                      
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ 
                          color: '#FFD700',
                          whiteSpace: 'nowrap'
                        }}>
                          {formatPrice((item.price * item.quantity).toFixed(2),order.currency,language)}
                        </Typography>
                        {isDiscounted && (
                          <Typography variant="caption" sx={{ 
                            color: '#FFD700',
                            display: 'block',
                            fontWeight: 600
                          }}>
                            {language === 'ar' ? 'توفير ' : 'Discount '} {formatPrice(itemSavings.toFixed(2),order.currency,language)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </ListItem>
                );
              })}
            </List>

            <Divider sx={{ 
              my: 2, 
              backgroundColor: '#2D2D2D'
            }} />

            {/* Order Totals */}
            <Box sx={{ 
              p: 2, 
              backgroundColor: '#0D0D0D',
              border: '1px solid #2D2D2D',
              borderRadius: '12px'
            }}>
              {totalSavings > 0 && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                      {language === 'ar' ? 'القيمة الأساسية:' : 'Oreginal value:'}
                    </Typography>
                    <Typography variant="body2" sx={{ textDecoration: 'line-through', color: '#6B6B6B' }}>
                      {formatPrice(originalTotal.toFixed(2),order.currency,language)}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ color: '#FFD700' }}>
                      <Discount sx={{ fontSize: 16, verticalAlign: 'text-bottom', mr: 0.5, color: '#FFD700' }} />
                      {language === 'ar' ? 'إجمالي التوفير :' : 'Total discount :'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#FFD700' }}>
                      -{formatPrice(totalSavings.toFixed(2),order.currency,language)}
                    </Typography>
                  </Box>
                  
                  <Divider sx={{ 
                    my: 1, 
                    backgroundColor: '#2D2D2D'
                  }} />
                </>
              )}
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography sx={{ color: '#B3B3B3' }}>{language === 'ar' ? 'رسوم التوصيل :' : 'Delivery fees :'}</Typography>
                <Typography sx={{ color: '#FFFFFF', fontWeight: 600 }}>
                  {formatPrice(order.delivery_fee,order.currency,language)}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography sx={{ color: '#B3B3B3' }}>{language === 'ar' ? 'رسوم الخدمة :' : 'ٍService charge :'}</Typography>
                <Typography sx={{ color: '#FFFFFF', fontWeight: 600 }}>
                  {formatPrice(order.service_charge,order.currency,language)}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1" fontWeight="bold" sx={{ color: '#FFFFFF' }}>
                  {language === 'ar' ? 'الإجمالي:' : 'Total:'}
                </Typography>
                <Typography variant="body1" fontWeight="bold" sx={{ color: '#FFD700' }}>
                  {formatPrice(order.total_amount,order.currency,language)}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                <Typography variant="h6" fontWeight="bold" sx={{ color: '#FFFFFF' }}>
                  {language === 'ar' ? 'إجمالي الطلب:' : 'Total order:'}
                </Typography>
                <Typography 
                  variant="h5" 
                  fontWeight="bold"
                  sx={{ 
                    color: '#FFD700',
                    fontWeight: 900,
                    fontSize: '1.5rem'
                  }}
                >
                  {formatPrice(order.total_amount,order.currency,language)}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Order Details Sidebar */}
        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Shipping Information */}
            <Card elevation={3} sx={{ 
              borderRadius: '16px',
              backgroundColor: '#0D0D0D',
              border: '1px solid #2D2D2D',
              '&:hover': {
                border: '1px solid #FFD700',
              }
            }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  color: '#FFD700',
                  fontWeight: 700
                }}>
                  <LocalShipping sx={{ color: '#FFD700' }} /> {language === 'ar' ? 'معلومات التوصيل' : 'Delivery information'}
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                    {language === 'ar' ? 'عنوان التوصيل:' : 'Delivery address:'}
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    mt: 1, 
                    p: 2, 
                    backgroundColor: '#1A1A1A',
                    border: '1px solid #2D2D2D',
                    borderRadius: '8px',
                    color: '#FFFFFF'
                  }}>
                    {order.shipping_address}
                  </Typography>
                </Box>
              </CardContent>
            </Card>

            {/* Payment Information */}
            <Card elevation={3} sx={{ 
              borderRadius: '16px',
              backgroundColor: '#0D0D0D',
              border: '1px solid #2D2D2D',
              '&:hover': {
                border: '1px solid #FFD700',
              }
            }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  color: '#FFD700',
                  fontWeight: 700
                }}>
                  <Payment sx={{ color: '#FFD700' }} /> {language === 'ar' ? 'معلومات الدفع' : 'Payment information'}
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                    {language === 'ar' ? 'طريقة الدفع':'Payment method'}
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    mt: 0.5,
                    color: '#FFFFFF',
                    fontWeight: 600
                  }}>
                    {order.payment_method.replace(/_/g, ' ').toUpperCase()}
                  </Typography>
                </Box>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                    {language === 'ar' ? 'تاريخ الطلب:':'Order date:'}
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    mt: 0.5,
                    color: '#FFFFFF'
                  }}>
                    {new Date(order.created_at).toLocaleDateString('ar-EG')}
                  </Typography>
                </Box>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <Card elevation={3} sx={{ 
              borderRadius: '16px',
              backgroundColor: '#0D0D0D',
              border: '1px solid #2D2D2D',
              '&:hover': {
                border: '1px solid #FFD700',
              }
            }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  color: '#FFD700',
                  fontWeight: 700
                }}>
                  <Store sx={{ color: '#FFD700' }} /> {language === 'ar' ? 'الإجراءات' : 'Actions'}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                  <Button
                    variant="contained"
                    endIcon={<Print />}
                    onClick={() => window.print()}
                    fullWidth
                    sx={{
                      backgroundColor: '#FFD700',
                      color: '#000000',
                      fontWeight: 700,
                      py: 1.5,
                      borderRadius: '12px',
                      '&:hover': {
                        backgroundColor: '#FFE55C',
                        boxShadow: '0 4px 12px rgba(255, 215, 0, 0.3)',
                      }
                    }}
                  >
                    {language === 'ar' ? 'طباعة الفاتورة' : 'Print invoice'}
                  </Button>
                  <Button
                    variant="outlined"
                    dir='rtl'
                    endIcon={<ArrowBack />}
                    onClick={() => navigate('/')}
                    fullWidth
                    sx={{
                      color: '#FFFFFF',
                      borderColor: '#2D2D2D',
                      py: 1.5,
                      borderRadius: '12px',
                      '&:hover': {
                        borderColor: '#FFD700',
                        backgroundColor: 'rgba(255, 215, 0, 0.08)',
                        color: '#FFD700',
                      }
                    }}
                  >
                    {language === 'ar' ? 'إستكمال التسوق' : 'Continue shopping'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default OrderConfirmation;